import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useDocumentViewer, docFileUrl } from '../../context/DocumentViewerContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText, Loader2, Upload, Link2, Trash2, Eye, Copy, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const DocumentsManagerModal = ({ open, onOpenChange }) => {
  const { getAuthHeader } = useAuth();
  const { openDocument } = useDocumentViewer();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [importUrl, setImportUrl] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/documents`, getAuthHeader());
      setDocs(res.data.documents || []);
    } catch { toast.error('Failed to load documents'); } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title || file.name.replace(/\.[^.]+$/, ''));
      fd.append('visibility', visibility);
      await axios.post(`${API}/documents/upload`, fd, { ...getAuthHeader(), headers: { ...(getAuthHeader().headers || {}), 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setTitle('');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); } finally { setBusy(false); }
  };

  const importFromUrl = async () => {
    if (!importUrl.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/documents/import-url`, { url: importUrl.trim(), title: title || null, visibility }, getAuthHeader());
      toast.success('File imported into ATLAS storage');
      setImportUrl(''); setTitle('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); } finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? Links to it will stop working.`)) return;
    try {
      await axios.delete(`${API}/documents/${d.id}`, getAuthHeader());
      toast.success('Document deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const copyLink = async (d) => {
    await navigator.clipboard.writeText(docFileUrl(d.slug || d.id));
    toast.success('ATLAS link copied — paste it into any carrier resource or guideline');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[760px]" data-testid="documents-manager-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Manrope'] text-xl font-extrabold text-[#2B3674]">
            <FolderOpen className="h-5 w-5 text-[#23ACD5]" /> ATLAS Documents
          </DialogTitle>
        </DialogHeader>
        <p className="-mt-2 text-xs text-[#A3AED0]">Files are stored privately and only served through your ATLAS domain to signed-in users.</p>

        <div className="grid gap-3 rounded-2xl border border-[#E9EDF7] bg-[#F4F7FE] p-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-1">
            <Label htmlFor="doc-title">Title (optional)</Label>
            <Input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Americo Underwriting Guide" data-testid="documents-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Who can view</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="documents-visibility-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All agents</SelectItem>
                <SelectItem value="admin">Admins only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className={`flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#2447D6]/30 bg-white px-4 py-3 text-sm font-bold text-[#2447D6] hover:bg-blue-50 ${busy ? 'pointer-events-none opacity-60' : ''}`} data-testid="documents-upload-dropzone">
            <Upload className="h-4 w-4" /> Upload PDF / file
            <input ref={fileRef} type="file" className="hidden" accept=".pdf,.docx,.xlsx,.xls,.pptx,.png,.jpg,.jpeg,.webp" onChange={(e) => upload(e.target.files?.[0])} data-testid="documents-file-input" />
          </label>
          <div className="flex gap-2">
            <Input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="https://… paste an old file link to import" data-testid="documents-import-url-input" />
            <button type="button" onClick={importFromUrl} disabled={busy || !importUrl.trim()} className="flex shrink-0 items-center gap-1.5 rounded-xl bg-[#2B3674] px-3 text-xs font-bold text-white disabled:opacity-50" data-testid="documents-import-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Import
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#2447D6]" /></div>
        ) : docs.length === 0 ? (
          <p className="py-8 text-center text-sm text-[#A3AED0]">No documents yet.</p>
        ) : (
          <div className="space-y-2" data-testid="documents-list">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-xl border border-[#E9EDF7] p-3" data-testid={`documents-row-${d.id}`}>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F4F7FE]"><FileText className="h-4 w-4 text-[#23ACD5]" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-[#2B3674]">{d.title}</p>
                  <p className="truncate text-[11px] text-[#A3AED0]">{d.file_type?.toUpperCase()} · {fmtSize(d.size)} · {d.visibility === 'admin' ? 'Admins only' : 'All agents'}{d.slug ? ` · ${d.slug}` : ''}</p>
                </div>
                <button type="button" onClick={() => openDocument({ id: d.id, title: d.title, filename: d.filename })} className="rounded-lg p-2 text-[#A3AED0] hover:bg-[#F4F7FE] hover:text-[#2447D6]" title="Open" data-testid={`documents-open-${d.id}`}><Eye className="h-4 w-4" /></button>
                <button type="button" onClick={() => copyLink(d)} className="rounded-lg p-2 text-[#A3AED0] hover:bg-[#F4F7FE] hover:text-[#2447D6]" title="Copy ATLAS link" data-testid={`documents-copy-${d.id}`}><Copy className="h-4 w-4" /></button>
                <button type="button" onClick={() => remove(d)} className="rounded-lg p-2 text-[#A3AED0] hover:bg-red-50 hover:text-red-500" title="Delete" data-testid={`documents-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
